import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Clock, Loader2, Calendar, MapPin, DollarSign, CreditCard, CalendarPlus } from 'lucide-react';
import { formatBookingReference, formatPaymentMethod } from '@/utils/bookingUtils';
import DateTimeDisplay from '../../components/booking/DateTimeDisplay';
import { buildBookingIcs, downloadIcs } from '../../lib/calendar';

export default function PaymentResult() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [location, setLocation] = useState(null);

  const bookingId = searchParams.get('booking_id');
  const orderId = searchParams.get('order_id') || searchParams.get('OrderId');
  const successIndicator = searchParams.get('success_indicator') || searchParams.get('successIndicator');

  const verifyPayment = useCallback(async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;

      // Build query params - preserve all parameters from URL
      const params = new URLSearchParams();
      if (orderId) params.append('order_id', orderId);
      if (successIndicator) params.append('success_indicator', successIndicator);

      // Get all query params from current URL to pass through
      const urlParams = new URLSearchParams(window.location.search);
      const message = urlParams.get('Message') || urlParams.get('message');
      const orderIdFromUrl = urlParams.get('OrderId') || urlParams.get('orderId');

      if (message) params.append('Message', message);
      if (orderIdFromUrl) params.append('OrderId', orderIdFromUrl);

      console.log('Verifying payment with params:', params.toString());

      const response = await fetch(
        `${API_URL}/api/bookings/${bookingId}/payment/verify?${params}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      // Parse response based on status
      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        // If JSON parsing fails, create error object
        console.error('JSON parsing error:', jsonError);
        data = {
          error: 'Failed to parse response',
          status: response.status
        };
      }

      if (!response.ok) {
        console.error('Verification failed:', data);
        throw new Error(data.detail || data.error || 'Failed to verify payment');
      }

      console.log('Payment verification result:', data);
      setResult(data);

      // Fetch location details
      if (data.location_id) {
        const locationResponse = await fetch(`${API_URL}/api/locations/${data.location_id}`);
        if (locationResponse.ok) {
          const locationData = await locationResponse.json();
          setLocation(locationData);
        }
      }

      if (data.is_success) {
        toast.success('Payment confirmed successfully!');
      } else if (data.payment_status === 'pending') {
        toast.info('Payment is being processed...');
      } else {
        toast.error('Payment was not successful');
      }

    } catch (err) {
      console.error('Payment verification error:', err);
      setError(err.message);
      toast.error(`Failed to verify payment: ${err.message}`);
    } finally {
      setVerifying(false);
    }
  }, [bookingId, orderId, successIndicator]);

  const lookupBookingByOrder = useCallback(async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/bookings/lookup-by-order/${orderId}`);

      if (response.ok) {
        const data = await response.json();
        // Update URL with booking_id and reload
        const params = new URLSearchParams(window.location.search);
        params.set('booking_id', data.booking_id);
        window.history.replaceState({}, '', `${window.location.pathname}?${params}`);

        // Now verify with the booking_id
        verifyPayment();
      } else {
        throw new Error('Failed to lookup booking');
      }
    } catch (err) {
      console.error('Booking lookup error:', err);
      setError('Failed to find booking for this payment');
      setVerifying(false);
    }
  }, [orderId, verifyPayment]);

  useEffect(() => {
    if (bookingId) {
      verifyPayment();
    } else if (orderId) {
      // If no booking_id but we have orderId, look it up
      lookupBookingByOrder();
    } else {
      setError('Missing booking information');
      setVerifying(false);
    }
  }, [bookingId, orderId, verifyPayment, lookupBookingByOrder]);

  // New function to check payment status with Pocket Pay gateway
  const checkGatewayStatus = async () => {
    setVerifying(true);
    setError(null);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      console.log('Checking payment status with Pocket Pay gateway...');
      
      const response = await fetch(
        `${API_URL}/api/bookings/${bookingId}/payment/check-gateway-status`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        try {
          data = await response.json();
        } catch (jsonError) {
          console.error('JSON parse error:', jsonError);
          throw new Error('Invalid server response');
        }
      } else {
        throw new Error('Server returned non-JSON response');
      }

      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Failed to check payment status');
      }

      console.log('Gateway status check result:', data);
      
      // Update UI based on gateway response
      if (data.status === 'confirmed') {
        // Payment confirmed!
        setResult({
          is_success: true,
          payment_status: 'confirmed',
          message: data.message,
          status_details: { message: data.message }
        });
        toast.success('Payment confirmed!');
        
        // Reload page to show updated booking details
        setTimeout(() => window.location.reload(), 1500);
      } else if (data.status === 'failed') {
        // Payment failed or timeout
        setResult({
          is_success: false,
          payment_status: 'failed',
          message: data.message,
          status_details: { message: data.message }
        });
        toast.error(data.message);
      } else if (data.status === 'pending') {
        // Still pending
        toast.info('Payment still pending. Please try again in a few minutes.');
      } else if (data.status === 'error') {
        // Error checking status
        setError(data.message || 'Failed to check payment status');
        toast.error(data.message || 'Failed to check payment status');
      }
      
    } catch (err) {
      console.error('Gateway status check error:', err);
      setError(err.message);
      toast.error(err.message);
    } finally {
      setVerifying(false);
    }
  };


  const handleAddToCalendar = () => {
    const reference = formatBookingReference(bookingId);
    const ics = buildBookingIcs({
      bookingDate: result.booking_date,
      timeSlot: result.time_slot,
      duration: result.duration,
      reference,
      locationName: location?.name,
      locationAddress: location?.address,
      amount: result.amount,
    });
    downloadIcs(`${reference || 'bba-booking'}.ics`, ics);
    toast.success('Calendar invite downloaded');
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 text-center">
          <Loader2 className="w-16 h-16 mx-auto mb-4 text-blue-500 animate-spin" />
          <h2 className="text-xl font-semibold mb-2">Verifying Payment</h2>
          <p className="text-gray-600">Please wait while we confirm your payment...</p>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-gray-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 bba-card bba-card-accent">
          <XCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h2 className="text-2xl font-bold text-center mb-2 text-red-600">Verification Error</h2>
          <p className="text-gray-600 text-center mb-6">{error}</p>
          <div className="flex gap-3">
            <Button 
              onClick={() => navigate('/bookings')}
              variant="outline"
              className="flex-1"
            >
              View Bookings
            </Button>
            <Button 
              onClick={() => navigate('/')}
              className="flex-1"
            >
              Go Home
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (!result) {
    return null;
  }

  const isSuccess = result.is_success;
  const isPending = result.payment_status === 'pending' || result.payment_status === 'processing';
  // Extracted from nested-ternary in JSX for readability + ESLint compliance.
  const bgGradient = isSuccess
    ? 'from-green-50 to-gray-100'
    : isPending
      ? 'from-yellow-50 to-gray-100'
      : 'from-red-50 to-gray-100';
  const statusColor = isSuccess
    ? 'text-green-600'
    : isPending
      ? 'text-yellow-600'
      : 'text-red-600';

  return (
    <div className={`min-h-screen bg-gradient-to-br ${bgGradient} flex items-center justify-center p-4`}>
      <Card className="w-full max-w-2xl p-8 bba-card bba-card-accent">
        {/* Status Icon and Header */}
        <div className="text-center mb-6">
          {isSuccess && (
            <>
              <CheckCircle className="w-20 h-20 mx-auto mb-4 text-green-500" />
              <h1 className="text-3xl font-bold text-green-600 mb-2">Payment Successful!</h1>
              <p className="text-gray-600">Your booking has been confirmed</p>
            </>
          )}
          
          {isPending && (
            <>
              <Clock className="w-20 h-20 mx-auto mb-4 text-yellow-500" />
              <h1 className="text-3xl font-bold text-yellow-600 mb-2">Payment Processing</h1>
              <p className="text-gray-600">Your payment is being verified. Please check back shortly.</p>
            </>
          )}
          
          {!isSuccess && !isPending && (
            <>
              <XCircle className="w-20 h-20 mx-auto mb-4 text-red-500" />
              <h1 className="text-3xl font-bold text-red-600 mb-2">Payment Failed</h1>
              <p className="text-gray-600">
                {result.status_details?.message || 'Your payment could not be processed'}
              </p>
            </>
          )}
        </div>

        {/* Payment Details */}
        <div className="bg-gray-50 rounded-lg p-6 mb-6 space-y-4">
          <h3 className="font-semibold text-lg mb-3">Payment Details</h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Transaction ID</p>
              <p className="font-mono text-sm">{result.transaction_id || 'N/A'}</p>
            </div>
            
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <p className={`font-semibold ${statusColor}`}>
                {result.payment_status?.toUpperCase()}
              </p>
            </div>
          </div>

          <div className="border-t pt-4 mt-4">
            <div className="flex items-start gap-3 mb-3">
              <Calendar className="w-5 h-5 text-gray-400 mt-1" />
              <div>
                <p className="text-sm text-gray-500">Booking Date & Time</p>
                <DateTimeDisplay
                  date={result.booking_date}
                  timeSlot={result.time_slot}
                  duration={result.duration}
                  className="mt-1"
                  testId="payment-result"
                />
              </div>
            </div>

            {location && (
              <div className="flex items-start gap-3 mb-3">
                <MapPin className="w-5 h-5 text-gray-400 mt-1" />
                <div>
                  <p className="text-sm text-gray-500">Location</p>
                  <p className="font-semibold">{location.name}</p>
                  <p className="text-sm text-gray-600">{location.address}</p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3 mb-3">
              <DollarSign className="w-5 h-5 text-gray-400 mt-1" />
              <div>
                <p className="text-sm text-gray-500">Amount Paid</p>
                <p className="font-semibold text-lg">BND ${result.amount?.toFixed(2)}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CreditCard className="w-5 h-5 text-gray-400 mt-1" />
              <div>
                <p className="text-sm text-gray-500">Payment Method</p>
                <p className="font-semibold">{formatPaymentMethod(result.payment_method)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Booking Reference for Customer Records */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-800">
            <span className="font-semibold">Booking Reference:</span> {formatBookingReference(bookingId)}
          </p>
          <p className="text-xs text-blue-600 mt-1">
            Please save this reference number for your records
          </p>
        </div>

        {/* Action Buttons */}
        {isSuccess && (
          <Button
            type="button"
            onClick={handleAddToCalendar}
            variant="outline"
            className="w-full mb-3 border-blue-300 text-blue-800 hover:bg-blue-50"
            data-testid="add-to-calendar-btn"
          >
            <CalendarPlus className="w-4 h-4 mr-2" />
            Add to Calendar
          </Button>
        )}
        <div className="flex gap-3">
          {isSuccess && (
            <>
              <Button 
                onClick={() => navigate('/bookings')}
                className="flex-1"
              >
                View My Bookings
              </Button>
              <Button 
                onClick={() => navigate('/book')}
                variant="outline"
                className="flex-1"
              >
                Book Another Court
              </Button>
            </>
          )}

          {isPending && (
            <>
              <Button 
                onClick={checkGatewayStatus}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                disabled={verifying}
              >
                {verifying ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Checking...</span>
                  </div>
                ) : (
                  'Check Payment Status'
                )}
              </Button>
              <Button 
                onClick={() => navigate('/bookings')}
                variant="outline"
                className="flex-1"
              >
                View Bookings
              </Button>
            </>
          )}

          {!isSuccess && !isPending && (
            <>
              <Button 
                onClick={() => navigate(`/payment/${bookingId}`)}
                className="flex-1"
              >
                Try Again
              </Button>
              <Button 
                onClick={() => navigate('/bookings')}
                variant="outline"
                className="flex-1"
              >
                View Bookings
              </Button>
            </>
          )}
        </div>

        {/* Additional Info */}
        {isSuccess && (
          <p className="text-center text-sm text-gray-500 mt-6">
            A confirmation has been logged. Please arrive 10 minutes before your booking time.
          </p>
        )}

        {!isSuccess && !isPending && (
          <p className="text-center text-sm text-gray-500 mt-6">
            If you were charged, the amount will be refunded within 5-7 business days.
            Please contact support if you need assistance.
          </p>
        )}
      </Card>
    </div>
  );
}
